/**
 * MM Defense System — Core Types
 *
 * All shared types/interfaces for the market-making defense system.
 * Multi-game strategy-first design: one config targets multiple games.
 *
 * Division of labor: We build infrastructure + API contracts.
 * Domino plugs in optimized thresholds + algo-driven action selection.
 */

// ── Venue (re-export to keep mm-defense self-contained) ──

export type Venue = 'kalshi' | 'polymarket';

// ── Fill Types ──

export interface MMFill {
  fillId: string;
  orderId: string;
  ticker: string;
  venue: Venue;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  quantity: number;
  /** Price in cents (1-99) */
  priceCents: number;
  /** Fee in cents per contract */
  feeCents: number;
  timestampMs: number;
}

export interface FillPair {
  id: string;
  ticker: string;
  venue: Venue;
  openFill: MMFill;
  closeFill: MMFill;
  durationMs: number;
  quantity: number;
  /** Gross P&L per contract in cents (before fees) */
  grossPnlCentsPerContract: number;
  /** Net P&L per contract in cents (after fees) */
  netPnlCentsPerContract: number;
  /** Net P&L in dollars for the whole pair */
  netPnlDollars: number;
}

export interface UnpairedFill {
  fill: MMFill;
  /** How long this fill has been unpaired */
  ageMs: number;
  /** Current mid price in cents, null if unavailable */
  currentMidCents: number | null;
  /** Price drift since fill in cents (negative = adverse), null if no mid */
  driftCents: number | null;
}

export interface PairStats {
  totalPairs: number;
  totalUnpaired: number;
  /** Fraction of fills that have been paired (0-1) */
  pairRate: number;
  avgRoundTripPnlDollars: number;
  avgDurationMs: number;
  winCount: number;
  lossCount: number;
  /** Win rate (0-1) */
  winRate: number;
  totalPnlDollars: number;
  /** Gross profit / gross loss (Infinity if no losses, 0 if no wins) */
  profitFactor: number;
}

export interface PairTrackerConfig {
  /** Maximum age in ms before an unpaired fill is considered stale (default: 300_000 = 5 min) */
  maxUnpairedAgeMs: number;
}

export const DEFAULT_PAIR_TRACKER_CONFIG: PairTrackerConfig = {
  maxUnpairedAgeMs: 300_000,
};

// ── Adverse Flow ──

export interface AdverseFlowScore {
  fillId: string;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  /** Price at which the fill occurred */
  fillPriceCents: number;
  /** Mid price after the scoring window, null if unavailable */
  postFillMidCents: number | null;
  /** How much the price moved against the fill (positive = adverse), null if no mid */
  adverseMovementCents: number | null;
  /** Whether this fill is classified as adversely selected */
  isAdverse: boolean;
  /** Time window used for scoring */
  windowMs: number;
}

export type FlowPattern = 'normal' | 'elevated' | 'systematic' | 'toxic';

export interface SideStats {
  count: number;
  adverseCount: number;
  adverseRate: number;
  avgMovementCents: number;
}

export interface AdverseFlowSummary {
  scoredCount: number;
  adverseCount: number;
  /** Fraction of fills that are adverse (0-1) */
  adverseRate: number;
  /** Average adverse movement in cents (only adverse fills) */
  avgAdverseMovementCents: number;
  /** Average favorable movement in cents (only favorable fills) */
  avgFavorableMovementCents: number;
  pattern: FlowPattern;
  bySide: { yes: SideStats; no: SideStats };
}

export interface AdverseFlowConfig {
  /** Minimum movement in cents to classify as adverse (default: 1) */
  adverseThresholdCents: number;
  /** Time window in ms after fill to measure price movement (default: 5000) */
  defaultWindowMs: number;
}

export const DEFAULT_ADVERSE_FLOW_CONFIG: AdverseFlowConfig = {
  adverseThresholdCents: 1,
  defaultWindowMs: 5_000,
};

// ── Book Metrics (subset for triggers — avoids importing microstructure) ──

export interface MMBookMetrics {
  spreadCents: number | null;
  midPriceCents: number | null;
  bestBidCents: number | null;
  bestAskCents: number | null;
  bidDepthContracts: number;
  askDepthContracts: number;
  imbalance: number;
}

// ── Triggers ──

export type TriggerSeverity = 'tier1' | 'tier2' | 'tier3';

export type TriggerType =
  | 'unpaired_fill_timer'
  | 'post_fill_drift'
  | 'fill_side_imbalance'
  | 'time_to_fill_asymmetry'
  | 'book_pressure_skew'
  | 'volume_spike'
  | 'one_sided_volume'
  | 'spread_blowout'
  | 'correlated_market_move'
  | 'stale_quote'
  | 'exposure_breach';

export interface TriggerFiring {
  type: TriggerType;
  severity: TriggerSeverity;
  message: string;
  /** Tickers affected by this trigger */
  tickers: string[];
  /** The measured value that caused the trigger */
  value: number;
  /** The threshold that was exceeded */
  threshold: number;
  evaluatedAtMs: number;
}

export interface TriggerConfig {
  // unpaired_fill_timer: seconds before warning
  unpairedFillTimerSec: number;
  unpairedFillTimerCriticalSec: number;

  // post_fill_drift: ticks of adverse movement
  postFillDriftTicks: number;
  postFillDriftTicksCritical: number;

  // fill_side_imbalance: ratio threshold (e.g., 0.7 = 70% one side)
  fillSideImbalanceRatio: number;

  // time_to_fill_asymmetry: ratio of fast/slow side fill times
  timeToFillAsymmetryRatio: number;

  // book_pressure_skew: imbalance threshold (0-1 scale)
  bookPressureSkewThreshold: number;

  // volume_spike: multiple of 15m average
  volumeSpikeMultiple: number;

  // one_sided_volume: fraction of volume on one side (0-1)
  oneSidedVolumeRatio: number;

  // spread_blowout: multiple of normal spread
  spreadBlowoutMultiple: number;

  // correlated_market_move: cents of movement in correlated market
  correlatedMarketMoveCents: number;

  // stale_quote: seconds since last book update
  staleQuoteSec: number;
  staleQuoteCriticalSec: number;

  // exposure_breach: max dollars exposed per ticker
  exposureBreachDollars: number;
}

export const DEFAULT_TRIGGER_CONFIG: TriggerConfig = {
  unpairedFillTimerSec: 30,
  unpairedFillTimerCriticalSec: 90,

  postFillDriftTicks: 2,
  postFillDriftTicksCritical: 5,

  fillSideImbalanceRatio: 0.7,

  timeToFillAsymmetryRatio: 3.0,

  bookPressureSkewThreshold: 0.6,

  volumeSpikeMultiple: 3.0,

  oneSidedVolumeRatio: 0.8,

  spreadBlowoutMultiple: 2.5,

  correlatedMarketMoveCents: 5,

  staleQuoteSec: 10,
  staleQuoteCriticalSec: 30,

  exposureBreachDollars: 5000,
};

// ── Multi-Game Strategy ──

export interface StrategyTarget {
  ticker: string;
  gameKey: string;
  sport: string;
  awayCode: string;
  homeCode: string;
  enabled: boolean;
}

export interface VolumeSnapshot {
  total: number;
  yesSide: number;
  noSide: number;
}

export interface CorrelatedPrice {
  currentCents: number;
  previousCents: number;
}

export interface TriggerSnapshot {
  /** Which games this strategy covers */
  targets: StrategyTarget[];
  unpairedFills: UnpairedFill[];
  adverseScores: AdverseFlowScore[];
  recentFills: MMFill[];
  bookMetrics: Map<string, MMBookMetrics>;
  volume60s: Map<string, VolumeSnapshot>;
  volume15mAvg: Map<string, number>;
  normalSpread: Map<string, number>;
  lastUpdateMs: Map<string, number>;
  correlatedPrices: Map<string, CorrelatedPrice>;
  exposureByTicker: Map<string, number>;
  nowMs: number;
}

// ── Actions ──

export type ActionType =
  | 'cancel_exposed_side'
  | 'dynamic_retreat'
  | 'size_reduction'
  | 'cancel_both_sides'
  | 'cooldown'
  | 'cross_venue_hedge';

export type ActionPriority = 1 | 2 | 3 | 4 | 5;

export interface CancelExposedParams {
  type: 'cancel_exposed_side';
  tickers: string[];
  side: 'yes' | 'no' | 'both';
}

export interface DynamicRetreatParams {
  type: 'dynamic_retreat';
  tickers: string[];
  retreatTicks: number;
}

export interface SizeReductionParams {
  type: 'size_reduction';
  tickers: string[];
  reductionFactor: number;
}

export interface CancelBothParams {
  type: 'cancel_both_sides';
  tickers: string[];
}

export interface CooldownParams {
  type: 'cooldown';
  tickers: string[];
  durationMs: number;
}

export interface CrossVenueHedgeParams {
  type: 'cross_venue_hedge';
  tickers: string[];
  hedgeVenue: Venue;
}

export type ActionParams =
  | CancelExposedParams
  | DynamicRetreatParams
  | SizeReductionParams
  | CancelBothParams
  | CooldownParams
  | CrossVenueHedgeParams;

export interface DefenseAction {
  type: ActionType;
  priority: ActionPriority;
  description: string;
  causedBy: TriggerType[];
  params: ActionParams;
}

export type DefensePosture = 'green' | 'yellow' | 'orange' | 'red';

export interface GameDefenseState {
  posture: DefensePosture;
  triggerCount: number;
}

export interface DefenseStatus {
  posture: DefensePosture;
  activeTriggers: TriggerFiring[];
  recommendedActions: DefenseAction[];
  triggerCount: { tier1: number; tier2: number; tier3: number };
  /** Per-game breakdown (multi-game awareness) */
  byGame: Map<string, GameDefenseState>;
  evaluatedAtMs: number;
}

export interface ActionConfig {
  /** Ticks to retreat when dynamic_retreat fires (default: 1) */
  retreatTicks: number;
  /** Factor to reduce size by (0-1, default: 0.5 = halve size) */
  sizeReductionFactor: number;
  /** Cooldown duration in ms (default: 60_000 = 1 min) */
  cooldownDurationMs: number;
  /** Preferred hedge venue (default: polymarket) */
  hedgeVenue: Venue;
}

export const DEFAULT_ACTION_CONFIG: ActionConfig = {
  retreatTicks: 1,
  sizeReductionFactor: 0.5,
  cooldownDurationMs: 60_000,
  hedgeVenue: 'polymarket',
};
